import React, { useState } from 'react';
import { 
  User, 
  Mail, 
  MapPin, 
  Calendar, 
  Edit3, 
  Save, 
  X, 
  Camera,
  Award,
  TrendingUp,
  Flame,
  Star,
  Upload,
  Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import UserImpactHistory from './UserImpactHistory';

export default function Profile() {
  const { profile, user, updateProfile, uploadAvatar } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    username: '',
    location: ''
  });
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  if (!profile || !user) {
    return <LoadingSpinner />;
  }

  const handleEditStart = () => {
    setEditForm({
      username: profile.username,
      location: profile.location
    });
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditForm({
      username: '',
      location: ''
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await updateProfile(editForm);
      if (!error) {
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      const { error } = await uploadAvatar(file);
      if (error) {
        console.error('Error uploading avatar:', error);
        alert('Failed to upload avatar. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      alert('Failed to upload avatar. Please try again.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const achievements = [
    {
      id: 'ach-1',
      title: 'First Steps',
      description: 'Complete your first eco mission',
      icon: 'Sprout',
      unlockedAt: '2024-01-16',
      rarity: 'common'
    },
    {
      id: 'ach-2',
      title: 'Week Warrior',
      description: 'Complete missions for 7 days straight',
      icon: 'Calendar',
      unlockedAt: '2024-01-23',
      rarity: 'rare'
    }
  ];

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'rare': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'epic': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'legendary': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="space-y-8">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
        <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
          {/* Avatar Section */}
          <div className="relative">
            <img
              src={profile.avatar_url}
              alt={profile.username}
              className="w-32 h-32 rounded-full border-4 border-white/20 object-cover"
            />
            <div className="absolute bottom-2 right-2">
              <label className="bg-white text-gray-700 p-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors cursor-pointer flex items-center justify-center">
                {uploadingAvatar ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  disabled={uploadingAvatar}
                />
              </label>
            </div>
          </div>

          {/* Profile Info */}
          <div className="flex-1 text-center md:text-left">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Username</label>
                  <input
                    type="text"
                    value={editForm.username}
                    onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Location</label>
                  <input
                    type="text"
                    value={editForm.location}
                    onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
                    placeholder="Enter your location"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-white text-emerald-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center space-x-2"
                  >
                    <Save className="h-4 w-4" />
                    <span>{saving ? 'Saving...' : 'Save'}</span>
                  </button>
                  <button
                    onClick={handleEditCancel}
                    className="bg-white/10 text-white px-4 py-2 rounded-lg font-medium hover:bg-white/20 transition-colors flex items-center space-x-2"
                  >
                    <X className="h-4 w-4" />
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-center md:justify-start space-x-3 mb-2">
                  <h2 className="text-3xl font-bold">{profile.username}</h2>
                  <button
                    onClick={handleEditStart}
                    className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-emerald-100 text-lg mb-4">{profile.level}</p>
                
                <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-emerald-200" />
                    <span className="text-emerald-100">{user.email}</span>
                  </div>
                  {profile.location && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-emerald-200" />
                      <span className="text-emerald-100">{profile.location}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-emerald-200" />
                    <span className="text-emerald-100">Joined {formatDate(profile.created_at)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-2">
            <div className="bg-orange-100 p-2 rounded-lg">
              <Flame className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{profile.streak}</p>
              <p className="text-sm text-gray-600">Day Streak</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-2">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <div className="w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">C</span>
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{profile.eco_coins.toLocaleString()}</p>
              <p className="text-sm text-gray-600">EcoCoins</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-2">
            <div className="bg-blue-100 p-2 rounded-lg">
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{profile.level}</p>
              <p className="text-sm text-gray-600">Current Level</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-2">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Award className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{achievements.length}</p>
              <p className="text-sm text-gray-600">Achievements</p>
            </div>
          </div>
        </div>
      </div>

      {/* Environmental Impact History */}
      <UserImpactHistory />

      {/* Achievements Section */}
      <div className="bg-white rounded-2xl border border-gray-200 p-8">
        <div className="flex items-center space-x-3 mb-6">
          <Award className="h-6 w-6 text-purple-600" />
          <h3 className="text-2xl font-bold text-gray-900">Achievements</h3>
        </div>
        
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`border-2 rounded-xl p-4 ${getRarityColor(achievement.rarity)}`}
            >
              <div className="flex items-start space-x-3">
                <div className="bg-white p-2 rounded-lg">
                  <Star className="h-5 w-5 text-yellow-500" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">{achievement.title}</h4>
                  <p className="text-sm opacity-80 mb-2">{achievement.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium capitalize">{achievement.rarity}</span>
                    <span className="text-xs opacity-60">
                      {formatDate(achievement.unlockedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}